import { useMutation } from '@apollo/client/react'
import {
  DPAG_MAX_NAMES_PER_FORWARDING,
  type Source as SourceAddress,
} from '@getcaya/schemas'
import { Modal } from 'antd'
import { useState } from 'react'
import {
  UpdateSourceAddressDocument,
  UpdateSourceMetadataDocument,
} from '~app/graph/generated/graphql'
import Form from '../../../../components/Elements/Form'
import { useUpdatingContext } from '../useUpdatingContext'

/*
 * Hooks
 */

const useAddress = (
  sourceId: string,
  isBusiness: boolean
): {
  loading: boolean
  updateAddress: (newAddress: SourceAddress) => Promise<void>
} => {
  const { setUpdating } = useUpdatingContext()
  const [updateSourceMetadataMutation, { loading: loadingMetadataMutation }] =
    useMutation(UpdateSourceMetadataDocument)
  const [updateSourceAddressMutation, { loading: loadingAddressMutation }] =
    useMutation(UpdateSourceAddressDocument)

  return {
    loading: loadingMetadataMutation || loadingAddressMutation,
    updateAddress: async address => {
      setUpdating(sourceId, true)
      const { streetAddress, city, zip, countryCode, lines } = address
      try {
        if (isBusiness !== address.business) {
          await updateSourceMetadataMutation({
            variables: {
              metadata: { business: address.business },
              id: sourceId,
            },
          })
        }

        await updateSourceAddressMutation({
          variables: {
            id: sourceId,
            data: {
              streetAddress,
              city,
              zip,
              countryCode,
              lines: {
                set: lines,
              },
            },
          },
        })
      } finally {
        setUpdating(sourceId, false)
      }
    },
  }
}

/*
 * Component
 */

interface Props {
  sourceId: string
  address: SourceAddress
  isBusiness: boolean
}

const Address = ({ sourceId, address, isBusiness }: Props) => {
  const [editing, setEditing] = useState(false)
  const { updateAddress, loading } = useAddress(sourceId, isBusiness)

  return (
    <>
      <Modal
        destroyOnClose
        open={editing}
        onCancel={() => setEditing(false)}
        title="Edit Address"
        footer={null}
      >
        <Form
          initialValues={{ ...address, business: isBusiness }}
          formConfig={{
            maxCharsBusinessName: 60,
            maxCharsPrivateName: 60,
            maxNames: DPAG_MAX_NAMES_PER_FORWARDING,
          }}
          onSubmit={values =>
            updateAddress(values).then(() => setEditing(false))
          }
          onCancel={() => setEditing(false)}
        />
      </Modal>
      <div role="button" onClick={() => !loading && setEditing(true)}>
        <p style={{ marginTop: 0 }}>{address.streetAddress}</p>
        {`${address.zip} ${address.city}`}
      </div>
    </>
  )
}

export default Address
